import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useAutosave, AutosaveStatus } from "@/lib/useAutosave";
import { Loader2, Trash2, UserPlus, ShieldCheck, ArrowUp, ArrowDown } from "lucide-react";
import { TabMatrix } from "./TabMatrix";
import SearchBar from "@/components/SearchBar";

const LEVEL_LABEL = { none: "Hidden", read: "Read only", write: "Full access" };
const SORTABLE = [
  { key: "email", label: "Email" },
  { key: "name", label: "Name" },
  { key: "phone", label: "Phone" },
  { key: "role", label: "Role" },
];

export default function UsersRolesPage() {
  const { user, refreshPrincipal } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [areas, setAreas] = useState([]);
  const [access, setAccess] = useState({});
  const [invite, setInvite] = useState({ email: "", name: "", phone: "", role: "pm" });
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [landingPages, setLandingPages] = useState([]);
  const [landingByRole, setLandingByRole] = useState({});

  const roleLabel = useMemo(
    () => Object.fromEntries(roles.map((r) => [r.value, r.label])), [roles]);

  const [peopleQ, setPeopleQ] = useState("");
  const sortedUsers = useMemo(() => {
    const val = (u) => (sort.key === "role" ? (roleLabel[u.role] || u.role) : u[sort.key]) || "";
    const n = peopleQ.trim().toLowerCase();
    return [...users].filter((u) => !n || `${u.name || ""} ${u.email || ""} ${roleLabel[u.role] || u.role || ""}`.toLowerCase().includes(n)).sort((a, b) =>
      (sort.dir === "asc" ? 1 : -1) *
      String(val(a)).localeCompare(String(val(b)), undefined, { sensitivity: "base" }));
  }, [users, sort, roleLabel, peopleQ]);

  const toggleSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  const load = async () => {
    try {
      // role access reads for anyone who can see Settings (Management = read-only); the people list is admin-only
      const ra = await api.getRoleAccess();
      const u = await api.getUsers().catch(() => ({ users: null, roles: ra.roles || [] }));
      setPeopleOk(u.users !== null);
      setUsers(u.users || []);
      setRoles(u.roles || ra.roles || []);
      setLandingPages(u.landing_pages || []);
      setLandingByRole(u.landing_by_role || {});
      setAreas(ra.areas || []);
      setAccess(ra.access || {});
      setMatrix({ tabs: ra.tabs || [], tabAccess: ra.tab_access || {}, tabOverrides: ra.tab_overrides || {},
                  editable: !!ra.editable });
    } catch {
      toast.error("Only an admin can manage users");
    }
    setLoading(false);
  };

  const [matrix, setMatrix] = useState({ tabs: [], tabAccess: {}, tabOverrides: {}, editable: false });
  const [peopleOk, setPeopleOk] = useState(true);
  useEffect(() => { load(); }, []);

  const saveGrants = async (row, grants) => {
    setUsers((prev) => prev.map((u) => (u.email === row.email ? { ...u, grants } : u)));
    try { await api.setUserGrants(row.email, grants); toast.success(`${row.email} grants updated`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update grants"); load(); }
  };
  const saveUser = async (row, changes) => {
    const next = { ...row, ...changes };
    setUsers((prev) => prev.map((u) => (u.email === row.email ? next : u)));
    try {
      await api.updateUser(row.email, {
        email: next.email, name: next.name || "", phone: next.phone || "",
        role: next.role, active: next.active,
        // A role change can invalidate the saved landing page — drop it and let the
        // fallback pick the first page the new role can see.
        landing_page: (landingByRole[next.role] || []).includes(next.landing_page || "")
          ? next.landing_page : "",
      });
      toast.success(`${next.email} updated`);
      if (next.email === user?.email) refreshPrincipal();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update that user");
      load();
    }
  };

  const addUser = async () => {
    if (!invite.email.includes("@")) return toast.error("Enter a valid email address");
    try {
      await api.inviteUser({ ...invite, active: true });
      setInvite({ email: "", name: "", phone: "", role: "pm" });
      toast.success("User added");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add that user");
    }
  };

  const removeUser = (email) => {
    // Revoking access is not something to do on a mis-click.
    toast(`Remove ${email}?`, {
      description: "They lose access immediately. They stay on the field roster.",
      duration: 10000,
      action: {
        label: "Remove",
        onClick: async () => {
          try {
            await api.deleteUser(email);
            toast.success(`${email} removed`);
            load();
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not remove that user");
          }
        },
      },
      cancel: { label: "Cancel" },
      classNames: { actionButton: "!bg-red-600 !text-white hover:!bg-red-700" },
    });
  };

  // No Save button — each dropdown change saves itself.
  const { status: accessStatus, trigger: saveAccess } = useAutosave(async (next) => {
    try {
      const res = await api.saveRoleAccess(next);
      setAccess(res.access);
      refreshPrincipal();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save role access");
      load();
      throw e;
    }
  });

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center" data-testid="users-roles-loading">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const selectClass =
    "rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-800 focus:border-[#007BFF] focus:outline-none";

  return (
    <div className="page-body space-y-8" data-testid="users-roles-page">
      {/* Who can see what */}
      <section className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="h-4 w-4 text-[#007BFF]" />
          <h2 className="text-lg font-semibold text-slate-900">Role Access</h2>
          <AutosaveStatus status={accessStatus} className="ml-2" />
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Hidden sections don't appear in the sidebar. Read only lets a role look without editing.
          Admins always have full access. Changes save themselves.
        </p>
        <div className="space-y-2">
          {roles.map(({ value, label }) => (
            <div
              key={value}
              className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0"
              data-testid={`role-access-row-${value}`}
            >
              <span className="w-40 text-sm font-medium text-slate-800">{label}</span>
              {areas.map((area) => (
                <label key={area.value} className="flex items-center gap-2">
                  <span className="text-xs uppercase tracking-wide text-slate-500 w-20">
                    {area.label}
                  </span>
                  <select
                    className={selectClass}
                    disabled={value === "admin" || value === "management" || !matrix.editable}
                    value={access[value]?.[area.value] || "none"}
                    onChange={(e) =>
                      setAccess((prev) => {
                        const next = {
                          ...prev,
                          [value]: { ...prev[value], [area.value]: e.target.value },
                        };
                        saveAccess(next);
                        return next;
                      })
                    }
                    data-testid={`role-access-${value}-${area.value}`}
                  >
                    {Object.entries(LEVEL_LABEL).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* Tab-level matrix (ROLES-1) */}
      <section className="rounded-lg border border-slate-200 bg-white p-5" data-testid="tab-matrix-section">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="h-4 w-4 text-[#007BFF]" />
          <h2 className="text-lg font-semibold text-slate-900">Tabs &amp; Sub-tabs</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Fine-tune inside each area: every tab and sub-tab, per role. Cells inherit the area
          setting above until you set them. The server checks this on every request — a hidden
          tab is not just hidden, it is refused.
        </p>
        <TabMatrix roles={roles} tabs={matrix.tabs} tabAccess={matrix.tabAccess} tabOverrides={matrix.tabOverrides}
                   editable={matrix.editable}
                   onSaved={(res) => setMatrix((m) => ({ ...m, tabAccess: res.tab_access || m.tabAccess,
                                                          tabOverrides: res.tab_overrides || m.tabOverrides }))} />
      </section>

      {/* People — admins only; Management reads the access map above */}
      {!peopleOk && <p className="text-sm text-slate-500" data-testid="users-admin-only">User accounts and invitations are managed by an admin.</p>}
      {peopleOk && <section className="rounded-lg border border-slate-200 bg-white p-5">
        <h2 className="text-lg font-semibold text-slate-900 mb-1">People</h2>
        <p className="text-sm text-slate-500 mb-4">
          Only these email addresses can sign in. Removing someone revokes access immediately —
          they stay on the field roster, and editing that roster row no longer brings the login
          back. "Lands on" is the page they open at sign-in, from their own department.
        </p>

        <div className="flex flex-wrap items-center gap-2 mb-5">
          <input
            className={selectClass + " flex-1 min-w-[220px]"}
            placeholder="name@absinc.com"
            value={invite.email}
            onChange={(e) => setInvite({ ...invite, email: e.target.value })}
            data-testid="invite-email-input"
          />
          <input
            className={selectClass + " w-40"}
            placeholder="Full name"
            value={invite.name}
            onChange={(e) => setInvite({ ...invite, name: e.target.value })}
            data-testid="invite-name-input"
          />
          <input
            className={selectClass + " w-36"}
            placeholder="Phone"
            value={invite.phone}
            onChange={(e) => setInvite({ ...invite, phone: e.target.value })}
            data-testid="invite-phone-input"
          />
          <select
            className={selectClass}
            value={invite.role}
            onChange={(e) => setInvite({ ...invite, role: e.target.value })}
            data-testid="invite-role-select"
          >
            {roles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <button
            onClick={addUser}
            className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid="invite-user-btn"
          >
            <UserPlus className="h-4 w-4" /> Add
          </button>
        </div>

        <div className="mb-3 flex items-center gap-3">
          <SearchBar value={peopleQ} onChange={setPeopleQ} placeholder="Search name, email, role…" testId="users-search" width="w-72" />
          <span className="text-xs text-slate-500" data-testid="users-count">{sortedUsers.length} of {users.length}</span>
        </div>
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
              {SORTABLE.map(({ key, label }) => (
                <th key={key} className="py-2">
                  <button
                    onClick={() => toggleSort(key)}
                    className={`flex items-center gap-1 uppercase tracking-wide transition-colors hover:text-slate-800 ${
                      sort.key === key ? "text-slate-800 font-semibold" : ""}`}
                    data-testid={`users-sort-${key}`}
                  >
                    {label}
                    {sort.key === key && (sort.dir === "asc"
                      ? <ArrowUp className="h-3 w-3" />
                      : <ArrowDown className="h-3 w-3" />)}
                  </button>
                </th>
              ))}
              <th className="py-2">Lands on</th>
              <th className="py-2" title="Per-person grants on top of the role (SCH-1)">Grants</th>
              <th className="py-2">Active</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map((row) => (
              <tr key={row.email} className="border-b border-slate-100" data-testid={`user-row-${row.email}`}>
                <td className="py-2 text-slate-800">{row.email}</td>
                <td className="py-2 text-slate-600">{row.name}</td>
                <td className="py-2">
                  <input
                    className={selectClass + " w-36"}
                    placeholder="Phone"
                    defaultValue={row.phone || ""}
                    onBlur={(e) => {
                      if ((e.target.value || "") !== (row.phone || "")) {
                        saveUser(row, { phone: e.target.value });
                      }
                    }}
                    data-testid={`user-phone-${row.email}`}
                  />
                </td>
                <td className="py-2">
                  <select
                    className={selectClass}
                    value={row.role}
                    onChange={(e) => saveUser(row, { role: e.target.value })}
                    data-testid={`user-role-${row.email}`}
                  >
                    {roles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </td>
                <td className="py-2">
                  <select
                    className={selectClass}
                    value={row.landing_page || ""}
                    onChange={(e) => saveUser(row, { landing_page: e.target.value })}
                    data-testid={`user-landing-${row.email}`}
                  >
                    <option value="">Default</option>
                    {landingPages
                      .filter((lp) => (landingByRole[row.role] || []).includes(lp.key))
                      .map((lp) => <option key={lp.key} value={lp.key}>{lp.label}</option>)}
                  </select>
                </td>
                <td className="py-2">
                  <div className="flex items-center gap-1" data-testid={`user-grants-${row.email}`}>
                    {[["scheduler", "Scheduler"], ["sr_foreman", "Sr. Foreman"]].map(([g, label]) => {
                      const on = (row.grants || []).includes(g);
                      return (
                        <button key={g} type="button" data-testid={`user-grant-${g}-${row.email}`} data-on={on ? "1" : "0"}
                                onClick={() => saveGrants(row, on ? (row.grants || []).filter((x) => x !== g) : [...(row.grants || []), g])}
                                className={`text-[10px] rounded-full border px-2 py-0.5 ${on ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </td>
                <td className="py-2">
                  <input
                    type="checkbox"
                    checked={!!row.active}
                    onChange={(e) => saveUser(row, { active: e.target.checked })}
                    data-testid={`user-active-${row.email}`}
                  />
                </td>
                <td className="py-2 text-right">
                  {row.email !== user?.email && (
                    <button
                      onClick={() => removeUser(row.email)}
                      className="text-slate-400 hover:text-red-600"
                      title="Remove user"
                      data-testid={`user-delete-${row.email}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>}
    </div>
  );
}
